import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, DEMO_BENEFITS } from "../demoData";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

const DECISIONS = {
  continue:  { label: "CONTINUE",  color: "#00E676", bg: "rgba(0,230,118,0.12)"  },
  invest:    { label: "INVEST+",   color: "#00D4FF", bg: "rgba(0,212,255,0.12)"  },
  monitor:   { label: "MONITOR",   color: "#FFB800", bg: "rgba(255,184,0,0.12)"  },
  redirect:  { label: "REDIRECT",  color: "#FF6B35", bg: "rgba(255,107,53,0.12)" },
  kill:      { label: "KILL",      color: "#FF3D57", bg: "rgba(255,61,87,0.12)"  },
};

function getDecision(project, benefits) {
  const b = benefits.find(b => b.project === project.id);
  const roiScore = b ? (b.projected / project.budget) * 100 : 0;
  if (project.failProb > 70 && project.cpi < 0.85)   return "kill";
  if (project.failProb > 50 && project.cpi < 0.90)   return "redirect";
  if (project.failProb < 15 && roiScore > 150)        return "invest";
  if (project.failProb < 20 && project.cpi >= 1.0)    return "continue";
  return "monitor";
}

export default function PortfolioRationalization() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(null);

  const enriched = DEMO_PROJECTS.map(p => {
    const b = DEMO_BENEFITS.find(b => b.project === p.id);
    const roi = b ? Math.round((b.projected / p.budget) * 100) : 0;
    const decision = getDecision(p, DEMO_BENEFITS);
    const strategicScore = p.strategic === "CRITICAL" ? 95 : p.strategic === "HIGH" ? 75 : 50;
    return { ...p, roi, decision, strategicScore, benefitsProjected: b?.projected || 0, confidence: b?.confidence || 50 };
  });

  const killCount     = enriched.filter(p => p.decision === "kill").length;
  const redirectCount = enriched.filter(p => p.decision === "redirect").length;
  const investCount   = enriched.filter(p => p.decision === "invest").length;
  const continueCount = enriched.filter(p => p.decision === "continue").length;

  const totalKillSavings = enriched.filter(p => p.decision === "kill").reduce((s, p) => s + (p.budget - p.spent), 0);

  const scatterData = enriched.map(p => ({
    x: p.failProb,
    y: p.roi,
    z: p.budget / 100000,
    name: p.name,
    id: p.id,
    decision: p.decision,
    strategic: p.strategic,
  }));

  const decisionGroups = Object.keys(DECISIONS).map(d => ({
    decision: d,
    count: enriched.filter(p => p.decision === d).length,
    totalBudget: enriched.filter(p => p.decision === d).reduce((s, p) => s + p.budget, 0),
    ...DECISIONS[d],
  })).filter(g => g.count > 0);

  return (
    <PageLayout
      title="Portfolio Rationalization"
      subtitle="Kill/Continue/Redirect decision engine, investment vs return scoring — P-10"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="KILL Recommended"     value={killCount}     icon="✗" color={theme.danger}  sub={`Potential savings: ${fmt(totalKillSavings)}`} />
        <KPICard label="REDIRECT Required"    value={redirectCount} icon="↺" color="#FF6B35"       sub="Change strategy/scope" />
        <KPICard label="INVEST+ Opportunity"  value={investCount}   icon="▲" color={theme.cyan}    sub="Accelerate investment" />
        <KPICard label="CONTINUE Confirmed"   value={continueCount} icon="✓" color={theme.success} sub="On track for benefits" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: "20px", marginBottom: "20px" }}>
        {/* ROI vs Failure Risk Matrix */}
        <div className="pmo-card">
          <div className="section-title">Portfolio Decision Matrix — ROI vs Failure Risk</div>
          <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "6px" }}>X = Failure Risk % · Y = ROI % · Bubble = Budget size</div>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis type="number" dataKey="x" name="Fail Risk" domain={[0, 90]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "Failure Risk %", fill: theme.textMuted, fontSize: 10, position: "bottom", offset: -5 }} />
              <YAxis type="number" dataKey="y" name="ROI %" domain={[0, 250]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "ROI %", angle: -90, fill: theme.textMuted, fontSize: 10 }} />
              <ZAxis type="number" dataKey="z" range={[30, 200]} />
              <Tooltip content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                const dec = DECISIONS[d.decision];
                return (
                  <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "10px 14px", borderRadius: "8px", fontSize: "11px", maxWidth: "220px" }}>
                    <div style={{ color: dec.color, fontWeight: 700, marginBottom: "4px" }}>{d.id}: {d.name}</div>
                    <div>Fail Risk: {d.x}% · ROI: {d.y}%</div>
                    <div>Strategic: {d.strategic}</div>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", background: dec.bg, color: dec.color, fontSize: "10px", fontWeight: 700 }}>{dec.label}</span>
                  </div>
                );
              }} />
              <Scatter data={scatterData}
                shape={(props) => {
                  const { cx, cy, r, payload } = props;
                  const dec = DECISIONS[payload.decision];
                  return (
                    <g onClick={() => setSelected(enriched.find(p => p.id === payload.id))} style={{ cursor: "pointer" }}>
                      <circle cx={cx} cy={cy} r={Math.max(r / 2, 8)} fill={dec.bg} stroke={dec.color} strokeWidth={2} />
                      <text x={cx} y={cy + 4} textAnchor="middle" fill={dec.color} fontSize="9" fontWeight="700">{payload.id}</text>
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Decision Summary */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {decisionGroups.map(g => (
            <div key={g.decision} className="pmo-card" style={{ borderLeft: `3px solid ${g.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "12px", color: g.color, fontWeight: 700 }}>{g.label}</span>
                  <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "2px" }}>{g.count} project{g.count !== 1 ? "s" : ""} · {fmt(g.totalBudget)} total budget</div>
                </div>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "22px", color: g.color, fontWeight: 700 }}>{g.count}</span>
              </div>
            </div>
          ))}
          <div className="pmo-card" style={{ background: `${theme.danger}10`, borderColor: `${theme.danger}33` }}>
            <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px" }}>Potential Kill Savings</div>
            <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "20px", color: theme.danger, fontWeight: 700 }}>{fmt(totalKillSavings)}</div>
            <div style={{ fontSize: "10px", color: theme.textMuted }}>Available for reallocation</div>
          </div>
        </div>
      </div>

      {/* Selected Project Decision Brief */}
      {selected && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: DECISIONS[selected.decision].color }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="section-title" style={{ marginBottom: 0 }}>{selected.id} — {selected.name}</div>
              <span style={{ padding: "4px 14px", borderRadius: "12px", background: DECISIONS[selected.decision].bg, color: DECISIONS[selected.decision].color, fontFamily: "'Orbitron',monospace", fontSize: "12px", fontWeight: 700 }}>
                {DECISIONS[selected.decision].label}
              </span>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "12px" }}>
            {[
              { l: "Failure Risk",   v: `${selected.failProb}%`,         c: selected.failProb > 50 ? theme.danger : theme.warning },
              { l: "ROI Score",      v: `${selected.roi}%`,              c: selected.roi > 100 ? theme.success : theme.warning },
              { l: "Strategic Fit",  v: selected.strategic,              c: selected.strategic === "CRITICAL" ? theme.danger : selected.strategic === "HIGH" ? theme.accent : theme.textSecondary },
              { l: "CPI",            v: selected.cpi.toFixed(3),         c: selected.cpi >= 1 ? theme.success : theme.danger },
            ].map((item, i) => (
              <div key={i} style={{ padding: "10px 14px", background: theme.bg, borderRadius: "8px", border: `1px solid ${theme.cardBorder}44` }}>
                <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "3px" }}>{item.l}</div>
                <div style={{ fontSize: "16px", color: item.c, fontWeight: 700, fontFamily: "'Orbitron',monospace" }}>{item.v}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: "12px 16px", background: `${DECISIONS[selected.decision].color}10`, border: `1px solid ${DECISIONS[selected.decision].color}33`, borderRadius: "8px" }}>
            <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "6px", fontWeight: 600 }}>AI DECISION RATIONALE</div>
            <div style={{ fontSize: "12px", color: theme.textSecondary }}>
              {selected.decision === "kill"
                ? `Project ${selected.id} is recommended for immediate termination. Failure probability (${selected.failProb}%) exceeds acceptable threshold with CPI of ${selected.cpi.toFixed(2)} indicating continued cost overrun. Stopping now saves ${fmt(selected.budget - selected.spent)} in remaining budget.`
                : selected.decision === "redirect"
                ? `Project ${selected.id} requires strategic redirection. Current trajectory shows schedule slippage (SPI: ${selected.spi.toFixed(2)}) and cost overrun (CPI: ${selected.cpi.toFixed(2)}). Recommend scope re-evaluation, PM coaching, and monthly board checkpoint.`
                : selected.decision === "invest"
                ? `Project ${selected.id} is delivering exceptional value (ROI: ${selected.roi}%, CPI: ${selected.cpi.toFixed(2)}). Recommend increasing investment to accelerate delivery and capture benefits earlier than planned.`
                : selected.decision === "continue"
                ? `Project ${selected.id} is performing well (CPI: ${selected.cpi.toFixed(2)}, SPI: ${selected.spi.toFixed(2)}). Maintain current investment level and delivery cadence. Benefits realization on track.`
                : `Project ${selected.id} requires close monitoring. Some performance indicators below target but not yet at intervention threshold. Recommend weekly status review and risk mitigation escalation.`}
            </div>
          </div>
        </div>
      )}

      {/* Full Rationalization Table */}
      <div className="pmo-card">
        <div className="section-title">Portfolio Rationalization Register</div>
        <table>
          <thead>
            <tr>{["ID","Project","Programme","CPI","SPI","Fail Risk","ROI","Strategic","Budget","Remaining","Decision"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {enriched.sort((a, b) => {
              const order = { kill: 0, redirect: 1, monitor: 2, continue: 3, invest: 4 };
              return order[a.decision] - order[b.decision];
            }).map(p => {
              const dec = DECISIONS[p.decision];
              return (
                <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setSelected(p)}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{p.id}</td>
                  <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{p.name}</td>
                  <td style={{ fontSize: "11px" }}>{p.program}</td>
                  <td style={{ color: p.cpi >= 1 ? theme.success : p.cpi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.cpi.toFixed(2)}</td>
                  <td style={{ color: p.spi >= 1 ? theme.success : p.spi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.spi.toFixed(2)}</td>
                  <td style={{ color: p.failProb > 60 ? theme.danger : p.failProb > 30 ? theme.warning : theme.success, fontFamily: "'Share Tech Mono',monospace" }}>{p.failProb}%</td>
                  <td style={{ color: p.roi > 100 ? theme.success : theme.warning, fontFamily: "'Share Tech Mono',monospace" }}>{p.roi}%</td>
                  <td><span style={{ fontSize: "10px", padding: "2px 6px", background: `${theme.accent}15`, color: theme.accent, borderRadius: "4px" }}>{p.strategic}</span></td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.budget)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.budget - p.spent)}</td>
                  <td>
                    <span style={{ padding: "3px 10px", borderRadius: "12px", background: dec.bg, color: dec.color, fontFamily: "'Orbitron',monospace", fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px" }}>
                      {dec.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
